import { DEFENCE_ESTABLISHMENT, DEFENCE_ESTABLISHMENT_BUFFER, DEFENCE_SPECS, ELASTIC_ESTABLISHMENT_PER_MISSING_CITY, ESTABLISHMENT_CUT_PER_CITY_AHEAD, ESTABLISHMENT_MIN, FALLEN_DEFENCE_SHARE, INTERCEPTOR_TYPES, DEVELOPMENT_EFFECTS, SHELTER_ALLOWANCE, } from './constants.js';
import { defaultFacing } from './coverage.js';
import { magazineFor, sitingCap } from './metrics.js';
import { hashString } from './rng.js';
import { clusterStatus } from './victory.js';
const ZERO = { materials: 0, fuel: 0, credits: 0 };
export function isInterceptor(type) {
    return INTERCEPTOR_TYPES.includes(type);
}
/**
 * An id that says nothing about the unit: not its type, not its city, not the
 * order it was built in. Two independent hashes of a seed only the server
 * knows, so it is unique across the match (units can move between cities)
 * and cannot be predicted from the ones already seen.
 */
export function unitIdFor(seed, cityId, seq) {
    const a = hashString(`${seed}|${cityId}|${seq}`).toString(36);
    const b = hashString(`${seq}|${cityId}|${seed}|u`).toString(36);
    return `u${a}${b}`;
}
/** A new unit in the city's reserve, at full health and ammunition. */
export function createUnit(city, type, seed) {
    const spec = DEFENCE_SPECS[type];
    const magazine = magazineFor(city, type);
    const unit = {
        id: unitIdFor(seed, city.id, city.unitSeq++),
        type,
        x: -1,
        y: -1,
        shots: magazine > 0 ? Math.min(spec.shots, magazine) : spec.shots,
        ammo: magazine,
        maxAmmo: magazine,
        hp: spec.hp,
        maxHp: spec.hp,
        offlineFor: 0,
        origin: city.id,
        paid: { ...spec.cost },
    };
    // A jammer has no magazine, but it keeps the one it would bring to a refit.
    if (magazine === 0 && isInterceptor(type))
        unit.charge = 1;
    city.reserve.push(unit);
    return unit;
}
/** Every unit the city holds and could site: its belt and its reserve. */
export function heldUnits(city) {
    return [...city.defences, ...city.reserve].filter((u) => u.hp > 0);
}
/**
 * Live interceptors belonging to `origin`, wherever they are. `world` is every
 * city in the match, because a unit on loan is still in service at home.
 */
export function establishmentInService(world, origin) {
    let n = 0;
    for (const c of world) {
        for (const u of c.defences)
            if (u.hp > 0 && u.origin === origin && isInterceptor(u.type))
                n++;
        for (const u of c.reserve)
            if (u.hp > 0 && u.origin === origin && isInterceptor(u.type))
                n++;
    }
    return n;
}
/** Live shelters this city holds. They cannot leave it. */
export function sheltersHeld(city) {
    return heldUnits(city).filter((u) => u.type === 'shelter').length;
}
/**
 * The most interceptors a city may have in service, including units on loan:
 * the establishment, Fortress City's four, and the elastic places its cluster
 * gives it or takes from it this round (`elastic`, set at the round boundary).
 * A cut never goes below ESTABLISHMENT_MIN.
 */
export function establishmentCap(city) {
    const usual = usualEstablishment(city);
    const bonus = city?.elastic?.bonus ?? 0;
    return bonus < 0 ? Math.max(Math.min(usual, ESTABLISHMENT_MIN), usual + bonus) : usual + bonus;
}
/** The establishment a city has when its cluster is level: the base and Fortress City's four. */
export function usualEstablishment(city) {
    const integrated = city?.development.some((r) => r.id === 'defence_2' && r.done)
        ? DEVELOPMENT_EFFECTS.defence_2_slots : 0;
    return DEFENCE_ESTABLISHMENT + DEFENCE_ESTABLISHMENT_BUFFER + integrated;
}
/** Its own interceptors the city keeps out of service, past its establishment. */
export function inactiveCount(city) {
    return city.inactive?.length ?? 0;
}
/**
 * Every interceptor the city owns: in service anywhere (`establishmentInService`)
 * and inactive at home. Production counts this, so standing a unit down never
 * makes room to build another.
 */
export function establishmentOwned(world, city) {
    return establishmentInService(world, city.id) + inactiveCount(city);
}
/**
 * How many more of this item the city may build now. A fallen city builds
 * nothing; everything else is the establishment (interceptors) or the shelter
 * allowance, less what the city already owns: an inactive interceptor comes
 * back into service before a new one is built.
 */
export function establishmentRoom(world, city, item) {
    if (city.defeated)
        return 0;
    if (item === 'shelter')
        return Math.max(0, SHELTER_ALLOWANCE - sheltersHeld(city));
    return Math.max(0, establishmentCap(city) - establishmentOwned(world, city));
}
/** Whether either elastic rule is switched on. */
function elasticRules() {
    return ELASTIC_ESTABLISHMENT_PER_MISSING_CITY > 0 || ESTABLISHMENT_CUT_PER_CITY_AHEAD > 0;
}
/**
 * The elastic establishment a city has from its cluster's count:
 * ELASTIC_ESTABLISHMENT_PER_MISSING_CITY more places for each city its side is
 * down there (D19), or ESTABLISHMENT_CUT_PER_CITY_AHEAD fewer for each city it
 * is up (D20). None for a city that has fallen, one in strategic reserve, one
 * whose cluster is decided or level, and none while both rules are set to
 * zero. The count is of the cluster alone, never the war.
 */
export function elasticFor(city, cities) {
    if (city.defeated || city.cluster < 0 || !elasticRules())
        return null;
    const status = clusterStatus(cities).find((s) => s.cluster === city.cluster);
    return status ? elasticOf(city.faction, status) : null;
}
/** `elasticFor` for every city on the board, from one count of its clusters. */
export function elasticBoard(cities) {
    const status = new Map(clusterStatus(cities).map((s) => [s.cluster, s]));
    const out = new Map();
    for (const c of cities) {
        const s = c.defeated || c.cluster < 0 || !elasticRules() ? undefined : status.get(c.cluster);
        out.set(c.id, s ? elasticOf(c.faction, s) : null);
    }
    return out;
}
function elasticOf(faction, s) {
    if (s.resolved)
        return null;
    const own = faction === 'WEST' ? s.west : s.east;
    const foes = faction === 'WEST' ? s.east : s.west;
    const bonus = foes > own
        ? (foes - own) * ELASTIC_ESTABLISHMENT_PER_MISSING_CITY
        : -(own - foes) * ESTABLISHMENT_CUT_PER_CITY_AHEAD;
    return bonus !== 0 ? { own, foes, bonus } : null;
}
/** Health, then rounds, then id: the order in which units are worth less to keep in service. */
function keepLast(a, b) {
    const hp = (u) => (u.maxHp > 0 ? u.hp / u.maxHp : 0);
    return hp(a) - hp(b) || ammoFraction(a) - ammoFraction(b) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}
/**
 * Bring what the city has in service back into line with its establishment
 * (D19), after the establishment changed. Past it, its own interceptors go
 * inactive: those in reserve before those sited, the most battered first, and
 * a sited one leaves the belt. Short of it, inactive ones come back into the
 * reserve, the soundest first. Nothing is healed, refilled or destroyed, and a
 * unit on loan, borrowed or bequeathed is never touched.
 */
export function reconcileEstablishment(world, city) {
    const out = { demobilised: [], recommissioned: [] };
    if (city.defeated)
        return out;
    const cap = establishmentCap(city);
    const inService = establishmentInService(world, city.id);
    if (inService > cap) {
        const own = (u) => u.hp > 0 && u.origin === city.id && isInterceptor(u.type) && !u.loan && !u.bequeathed;
        const order = [...city.reserve.filter(own).sort(keepLast), ...city.defences.filter(own).sort(keepLast)];
        const going = new Set(order.slice(0, inService - cap));
        if (!going.size)
            return out;
        city.reserve = city.reserve.filter((u) => !going.has(u));
        city.defences = city.defences.filter((u) => !going.has(u));
        const inactive = city.inactive ?? [];
        for (const u of order.filter((x) => going.has(x))) {
            u.x = -1;
            u.y = -1;
            inactive.push(u);
            out.demobilised.push(u);
        }
        city.inactive = inactive;
    }
    else if (inService < cap && city.inactive?.length) {
        const back = new Set([...city.inactive].sort((a, b) => keepLast(b, a)).slice(0, cap - inService));
        for (const u of [...city.inactive].sort((a, b) => keepLast(b, a))) {
            if (!back.has(u))
                continue;
            city.reserve.push(u);
            syncMagazine(city, u);
            out.recommissioned.push(u);
        }
        city.inactive = city.inactive.filter((u) => !back.has(u));
        if (!city.inactive.length)
            delete city.inactive;
    }
    return out;
}
/**
 * Scrap what the city owns past a cut establishment (D20), after the
 * establishment fell for the side ahead: inactive units first, the most
 * battered of them, then at random among its own interceptors at home, sited
 * or in reserve. A unit on loan, borrowed or bequeathed is never touched, and
 * shelters are outside the establishment; so when everything left over is
 * out on loan, nothing more goes until it comes home. The units are gone, not
 * wrecked: nothing is left on the belt. Returns what went.
 */
export function cullEstablishment(world, city, rng) {
    const gone = [];
    // Only a cut scraps: past a bonus that lapsed, the excess is kept inactive.
    if (city.defeated || !city.elastic || city.elastic.bonus >= 0)
        return gone;
    const cap = establishmentCap(city);
    const own = (u) => u.hp > 0 && u.origin === city.id && isInterceptor(u.type) && !u.loan && !u.bequeathed;
    while (establishmentOwned(world, city) > cap) {
        if (city.inactive?.length) {
            const [u] = [...city.inactive].sort(keepLast);
            city.inactive = city.inactive.filter((x) => x !== u);
            if (!city.inactive.length)
                delete city.inactive;
            gone.push(u);
            continue;
        }
        const home = [...city.defences.filter(own), ...city.reserve.filter(own)]
            .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
        if (!home.length)
            break;
        const u = home[rng.int(home.length)];
        city.defences = city.defences.filter((x) => x !== u);
        city.reserve = city.reserve.filter((x) => x !== u);
        gone.push(u);
    }
    return gone;
}
/**
 * Bring an inactive interceptor back into service in place of one of the
 * city's own (a RECOMMISSION order): on its tile and facing if that one was
 * sited, in the reserve if not, and the other goes inactive. The count in
 * service does not change, so the establishment cannot be passed this way.
 * A reason when it cannot be done.
 */
export function recommissionInPlaceOf(city, unitId, replaces) {
    const inactive = city.inactive ?? [];
    const back = inactive.find((u) => u.id === unitId);
    if (!back)
        return 'that launcher is not inactive';
    const sited = city.defences.findIndex((u) => u.id === replaces);
    const spare = sited >= 0 ? -1 : city.reserve.findIndex((u) => u.id === replaces);
    const out = sited >= 0 ? city.defences[sited] : spare >= 0 ? city.reserve[spare] : undefined;
    if (!out || out.hp <= 0)
        return 'you do not hold the launcher it would replace';
    if (!isInterceptor(out.type))
        return 'a shelter is outside the establishment';
    if (out.origin !== city.id || out.bequeathed)
        return 'only the city\'s own launchers go inactive';
    if (out.loan)
        return 'a launcher on loan cannot go inactive';
    inactive.splice(inactive.indexOf(back), 1);
    if (sited >= 0) {
        back.x = out.x;
        back.y = out.y;
        if (out.facing != null)
            back.facing = out.facing;
        else
            delete back.facing;
        city.defences[sited] = back;
    }
    else {
        back.x = -1;
        back.y = -1;
        city.reserve[spare] = back;
    }
    out.x = -1;
    out.y = -1;
    inactive.push(out);
    city.inactive = inactive;
    syncMagazine(city, back);
    return null;
}
/** The fraction of a full magazine the unit carries, whatever its type. */
export function ammoFraction(u) {
    if (u.maxAmmo > 0)
        return u.ammo / u.maxAmmo;
    return u.charge ?? 1;
}
/**
 * Bring a unit's magazine depth into line with what the holding city can give
 * it (Fortress City deepens magazines). The rounds in it stay the rounds
 * in it: a deeper magazine is room for Produce to fill, not a refill.
 *
 * A borrowed unit keeps the magazine it was lent with. It goes home at round
 * end, and a borrower without Fortress City would otherwise cut it to its own
 * depth and throw away rounds the lender paid for. A bequeathed unit is its
 * new holder's for good, so it takes that city's depth like any other unit
 * the city holds.
 */
export function syncMagazine(city, u) {
    if (u.loan)
        return;
    const depth = magazineFor(city, u.type);
    if (depth === 0 || depth === u.maxAmmo)
        return;
    u.maxAmmo = depth;
    u.ammo = Math.min(u.ammo, depth);
}
/** What repurposing this unit to `to` costs: the price above what it has been paid up to. */
export function refitCost(u, to) {
    const price = DEFENCE_SPECS[to].cost;
    return {
        materials: Math.max(0, price.materials - u.paid.materials),
        fuel: Math.max(0, price.fuel - u.paid.fuel),
        credits: Math.max(0, price.credits - u.paid.credits),
    };
}
/**
 * Repurpose a unit in place. Health and ammunition keep their proportions
 * across the switch, rounded down, so no sequence of switches can heal a unit
 * or refill it: a long SAM at half a magazine becomes a C-RAM at half a
 * magazine, and a SAM that becomes a jammer (no magazine) carries its fraction
 * as `charge` until it becomes a launcher again.
 */
export function refitUnit(city, u, to) {
    const spec = DEFENCE_SPECS[to];
    const hpFraction = u.maxHp > 0 ? Math.max(0, u.hp) / u.maxHp : 0;
    const fraction = ammoFraction(u);
    u.type = to;
    u.maxHp = spec.hp;
    u.hp = Math.min(spec.hp, Math.floor(hpFraction * spec.hp * 1000 + 1e-6) / 1000);
    const depth = magazineFor(city, to);
    if (depth > 0) {
        u.maxAmmo = depth;
        u.ammo = Math.floor(fraction * depth + 1e-9);
        delete u.charge;
    }
    else {
        u.maxAmmo = 0;
        u.ammo = 0;
        u.charge = fraction;
    }
    u.shots = depth > 0 ? Math.min(spec.shots, u.ammo) : spec.shots;
    u.paid = {
        materials: Math.max(u.paid.materials, spec.cost.materials),
        fuel: Math.max(u.paid.fuel, spec.cost.fuel),
        credits: Math.max(u.paid.credits, spec.cost.credits),
    };
}
function affordable(purse, cost) {
    return purse.materials >= cost.materials && purse.fuel >= cost.fuel && purse.credits >= cost.credits;
}
function add(a, b) {
    return { materials: a.materials + b.materials, fuel: a.fuel + b.fuel, credits: a.credits + b.credits };
}
/** What a submission's refits would cost the treasury, all told. */
export function refitBill(city, refits) {
    const held = new Map(heldUnits(city).map((u) => [u.id, u]));
    let bill = { ...ZERO };
    for (const r of refits ?? []) {
        const u = held.get(r.unitId);
        if (u)
            bill = add(bill, refitCost(u, r.to));
    }
    return bill;
}
/**
 * Check a defence submission against what the city holds. `purse` is the
 * side's treasury, when the refits should be checked against it.
 */
export function validateDefence(city, c, purse) {
    const sent = new Set(c.transfers?.filter((t) => t.kind === 'unit').map((t) => t.unitId));
    if (c.placements.some((p) => sent.has(p.id)))
        return 'recall the launcher to reserve before lending it';
    if (c.refits?.some((r) => sent.has(r.unitId)))
        return 'do not repurpose a launcher that is being lent';
    const held = new Map(heldUnits(city).map((u) => [u.id, u]));
    const typeAfter = new Map([...held].map(([id, u]) => [id, u.type]));
    const refits = c.refits ?? [];
    if (refits.length > held.size)
        return 'more refits than units';
    const refitted = new Set();
    for (const r of refits) {
        const u = held.get(r.unitId);
        if (!u)
            return 'you do not hold that unit';
        if (u.type === 'shelter')
            return 'a shelter cannot be repurposed';
        if (!isInterceptor(r.to))
            return 'a unit can only be repurposed to another interceptor';
        if (refitted.has(r.unitId))
            return 'a unit can only be repurposed once a phase';
        refitted.add(r.unitId);
        typeAfter.set(r.unitId, r.to);
    }
    if (purse) {
        const bill = refitBill(city, refits);
        if (!affordable(purse, bill)) {
            return `the treasury cannot pay for those refits (${bill.materials} materials, ` +
                `${bill.fuel} fuel, ${bill.credits} credits)`;
        }
    }
    const seenUnits = new Set();
    const seenTiles = new Set();
    let interceptors = 0;
    for (const p of c.placements) {
        if (!held.has(p.id))
            return 'you do not hold that unit';
        if (seenUnits.has(p.id))
            return 'a unit cannot be sited twice';
        seenUnits.add(p.id);
        if (!Number.isInteger(p.x) || !Number.isInteger(p.y) ||
            p.x < 0 || p.y < 0 || p.x >= city.width || p.y >= city.height) {
            return 'placement is outside the city';
        }
        if (p.facing != null && (!Number.isInteger(p.facing) || p.facing < 0 || p.facing >= 360)) {
            return 'a facing is whole degrees from 0 to 359';
        }
        const tile = p.y * city.width + p.x;
        if (city.terrain[tile] === 'water')
            return 'you cannot site a launcher in the river';
        if (seenTiles.has(tile))
            return 'two units cannot share a tile';
        seenTiles.add(tile);
        if (typeAfter.get(p.id) !== 'shelter')
            interceptors++;
    }
    const cap = sitingCap(city);
    if (interceptors > cap) {
        return `you can only direct ${cap} launchers at once — your command network will not stretch further`;
    }
    return null;
}
/**
 * Put a validated submission into effect: posture, then refits (paid from the
 * side's treasury, in the order given), then the belt. Every held unit not
 * sited goes to the reserve with its state intact. Nothing here heals,
 * refills or moves a unit anywhere it was not told to go.
 */
export function applyDefence(city, c, purse) {
    const out = { refitted: [], unpaid: [] };
    city.posture = c.posture === 'ACTIVE' ? 'ACTIVE' : 'EMCON';
    const held = heldUnits(city);
    const byId = new Map(held.map((u) => [u.id, u]));
    const done = new Set();
    for (const r of c.refits ?? []) {
        const u = byId.get(r.unitId);
        if (!u || done.has(u.id) || u.type === 'shelter' || !isInterceptor(r.to) || u.type === r.to)
            continue;
        done.add(u.id);
        const cost = refitCost(u, r.to);
        if (!affordable(purse, cost)) {
            out.unpaid.push({ unitId: u.id, to: r.to });
            continue;
        }
        purse.materials -= cost.materials;
        purse.fuel -= cost.fuel;
        purse.credits -= cost.credits;
        const from = u.type;
        refitUnit(city, u, r.to);
        out.refitted.push({ unitId: u.id, from, to: r.to, cost });
    }
    // Sited in the order given, up to the cap; a tile or unit already taken is
    // skipped rather than doubled up. Validation refuses both, so this only
    // matters if the city changed between the check and now.
    const cap = sitingCap(city);
    const sited = [];
    const onMap = new Set();
    const tiles = new Set();
    let interceptors = 0;
    for (const p of c.placements) {
        const u = byId.get(p.id);
        if (!u || onMap.has(u))
            continue;
        if (p.x < 0 || p.y < 0 || p.x >= city.width || p.y >= city.height)
            continue;
        const tile = p.y * city.width + p.x;
        if (tiles.has(tile) || city.terrain[tile] === 'water')
            continue;
        if (u.type !== 'shelter' && interceptors >= cap)
            continue;
        if (u.type !== 'shelter')
            interceptors++;
        tiles.add(tile);
        onMap.add(u);
        u.x = p.x;
        u.y = p.y;
        // A launcher nobody turned faces straight out from the middle of the city.
        u.facing = p.facing != null && Number.isInteger(p.facing) && p.facing >= 0 && p.facing < 360
            ? p.facing
            : defaultFacing(city, p.x, p.y);
        sited.push(u);
    }
    const reserve = held.filter((u) => !onMap.has(u));
    for (const u of reserve) {
        u.x = -1;
        u.y = -1;
    }
    for (const u of held)
        syncMagazine(city, u);
    city.defences = sited;
    city.reserve = reserve;
    return out;
}
/**
 * What a fallen city's crews carry out to the rest of its side: a share of its
 * surviving interceptors (FALLEN_DEFENCE_SHARE), the healthiest first. Each
 * goes to whichever ally holds the fewest at that moment, so the thinnest
 * belts fill first, into its reserve with the health and rounds it had. It
 * stays marked as bequeathed: it counts against no establishment, and it
 * brings its own post with it (sitingCap). Call before destroyHeldUnits, which
 * takes whatever is left.
 */
export function bequeathUnits(fallen, side) {
    const allies = side.filter((c) => c.id !== fallen.id && !c.defeated);
    if (FALLEN_DEFENCE_SHARE <= 0 || allies.length === 0)
        return [];
    const fraction = (u) => (u.maxAmmo > 0 ? u.ammo / u.maxAmmo : 1);
    const units = heldUnits(fallen)
        .filter((u) => u.hp > 0 && isInterceptor(u.type) && !u.loan)
        .sort((a, b) => b.hp / b.maxHp - a.hp / a.maxHp || fraction(b) - fraction(a) || (a.id < b.id ? -1 : 1));
    const count = Math.floor(units.length * Math.min(1, FALLEN_DEFENCE_SHARE) + 1e-9);
    const held = (c) => heldUnits(c).filter((u) => u.hp > 0 && isInterceptor(u.type)).length;
    const out = [];
    for (const unit of units.slice(0, count)) {
        const to = allies.slice().sort((a, b) => held(a) - held(b) || (a.id < b.id ? -1 : 1))[0];
        const moved = { ...unit, bequeathed: true, offlineFor: 0 };
        to.reserve.push(moved);
        fallen.defences = fallen.defences.filter((u) => u !== unit);
        fallen.reserve = fallen.reserve.filter((u) => u !== unit);
        out.push({ to, unit: moved });
    }
    return out;
}
/**
 * A fallen city's units go with it, whoever they belong to. Each one frees a
 * place in its home city's establishment, which is how a lender gets back
 * what it lent to a city that did not survive.
 */
export function destroyHeldUnits(city) {
    const n = heldUnits(city).length + inactiveCount(city);
    city.defences = [];
    city.reserve = [];
    delete city.inactive;
    return n;
}
//# sourceMappingURL=defence.js.map